import { useMemo } from "react";
import { createFileRoute, Link, notFound, useRouter } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ExternalLink, Trophy, Ticket, ArrowRight } from "lucide-react";

import { Layout } from "@/components/Layout";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { InstantGameCard } from "@/components/InstantGameCard";
import { StateLogo } from "@/components/StateLogo";
import { buildSeo } from "@/lib/seo";
import { canonicalUrl } from "@/lib/seo-policy";
import { instantGameTitle, instantGameMetaDescription } from "@/lib/page-copy";
import {
  instantGameQO,
  instantGamesListQO,
} from "@/lib/instant-games.functions";
import { sanitizeHtml, stripHtml } from "@/lib/sanitize-html";
import "./instant-game-content.css";

const HIDDEN_CATEGORIES = new Set(["derby cash", "fast play"]);

function splitContentAndFaq(html: string): {
  body: string;
  faq: { q: string; a: string }[];
} {
  if (!html) return { body: "", faq: [] };
  const faqMatch = html.match(
    /<div class="game-faq-accordion">([\s\S]*?)<\/div>\s*$/i,
  );
  if (!faqMatch || faqMatch.index === undefined) {
    return { body: html, faq: [] };
  }
  const body = html.slice(0, faqMatch.index).trim();
  const inner = faqMatch[1];
  const items = [
    ...inner.matchAll(
      /<div class="accordion-item">\s*<button class="accordion-header">([\s\S]*?)<\/button>\s*<div class="accordion-content">([\s\S]*?)<\/div>\s*<\/div>/gi,
    ),
  ].map((m) => ({
    q: m[1]
      .replace(/<i\b[^>]*>[\s\S]*?<\/i>/gi, "")
      .replace(/<[^>]+>/g, "")
      .trim(),
    a: m[2].trim(),
  }));
  return { body, faq: items };
}

export const Route = createFileRoute("/states/$stateSlug/instant/$gameSlug")({
  loader: async ({ params, context }) => {
    const game = await context.queryClient.ensureQueryData(
      instantGameQO(params.stateSlug, params.gameSlug),
    );
    if (!game) throw notFound();
    // Prefetch list for the "More games" strip
    void context.queryClient.prefetchQuery(instantGamesListQO(params.stateSlug));
    return { game };
  },
  head: ({ params, loaderData }) => {
    const stateName = params.stateSlug
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c: string) => c.toUpperCase());
    const game = loaderData?.game;
    if (!game) {
      return buildSeo({
        title: `${stateName} Instant Game`,
        description: `${stateName} instant lottery game.`,
        path: `/states/${params.stateSlug}/instant/${params.gameSlug}`,
      });
    }
    const path = `/states/${params.stateSlug}/instant/${params.gameSlug}`;
    const copyInput = {
      name: game.name,
      slug: params.gameSlug,
      stateName,
      stateSlug: params.stateSlug,
      topPrize: game.top_prize,
      priceRange: game.price_range,
      categories: game.categories,
    };
    const title = instantGameTitle(copyInput);
    const desc = instantGameMetaDescription(copyInput);
    // Parse price_range like "$0.50 to $30.00" or "$5" into numbers for AggregateOffer
    const priceNums = (game.price_range ?? "")
      .match(/\d+(?:\.\d+)?/g)
      ?.map((n) => Number(n))
      .filter((n) => Number.isFinite(n)) ?? [];
    const lowPrice = priceNums.length ? Math.min(...priceNums) : undefined;
    const highPrice = priceNums.length ? Math.max(...priceNums) : undefined;
    const jsonLd: Array<Record<string, unknown>> = [
      {
        "@context": "https://schema.org",
        "@type": "Product",
        name: `${game.name} (${stateName} Scratch-Off)`,
        description: desc,
        ...(game.image_url ? { image: game.image_url } : {}),
        category: "Lottery Scratch-Off Ticket",
        brand: { "@type": "Organization", name: `${stateName} Lottery` },
        ...(lowPrice !== undefined
          ? {
              offers: {
                "@type": "AggregateOffer",
                priceCurrency: "USD",
                lowPrice: String(lowPrice),
                highPrice: String(highPrice),
                offerCount: priceNums.length,
                availability: "https://schema.org/InStock",
              },
            }
          : {}),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: canonicalUrl("/") },
          { "@type": "ListItem", position: 2, name: "States", item: canonicalUrl("/states") },
          {
            "@type": "ListItem",
            position: 3,
            name: `${stateName} Lottery`,
            item: canonicalUrl(`/states/${params.stateSlug}`),
          },
          { "@type": "ListItem", position: 4, name: game.name, item: canonicalUrl(path) },
        ],
      },
    ];
    return buildSeo({
      title,
      description: desc,
      path,
      image: game.image_url ?? undefined,
      jsonLd,
    });
  },
  component: InstantGamePage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <Layout>
        <div className="container py-20 text-center space-y-4">
          <h1 className="font-display text-2xl font-bold">Something went wrong</h1>
          <p className="text-muted-foreground">{error.message}</p>
          <Button
            onClick={() => {
              reset();
              router.invalidate();
            }}
          >
            Try again
          </Button>
        </div>
      </Layout>
    );
  },
  notFoundComponent: () => {
    const { stateSlug } = Route.useParams();
    return (
      <Layout>
        <div className="container py-20 text-center space-y-4">
          <h1 className="font-display text-2xl font-bold">Game not found</h1>
          <Button asChild>
            <Link to="/states/$stateSlug" params={{ stateSlug }}>
              Back to state page
            </Link>
          </Button>
        </div>
      </Layout>
    );
  },
});

function InstantGamePage() {
  const { stateSlug, gameSlug } = Route.useParams();
  const { data: game } = useSuspenseQuery(instantGameQO(stateSlug, gameSlug));
  const { data: allGames = [] } = useSuspenseQuery(instantGamesListQO(stateSlug));

  if (!game) return null;

  const stateName = stateSlug
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c: string) => c.toUpperCase());

  const { body, faq } = useMemo(
    () => splitContentAndFaq(game.game_content ?? ""),
    [game.game_content],
  );

  const more = allGames
    .filter(
      (g) =>
        g.slug !== game.slug &&
        !g.categories.some((c) =>
          HIDDEN_CATEGORIES.has(c.trim().toLowerCase()),
        ),
    )
    .slice(0, 20);

  // "Similar games" = shares at least one category with the current game,
  // excluding hidden categories. Falls back to any 6 visible games.
  const currentCats = new Set(
    game.categories
      .map((c) => c.trim().toLowerCase())
      .filter((c) => !HIDDEN_CATEGORIES.has(c)),
  );
  const similar = (
    currentCats.size > 0
      ? more.filter((g) =>
          g.categories.some((c) => currentCats.has(c.trim().toLowerCase())),
        )
      : more
  ).slice(0, 6);

  // Highest advertised top prizes in this state — descriptive cross-links that
  // aren't already covered by the "similar" (same-category) list.
  const prizeValue = (s: string | null) => {
    const n = Number((s ?? "").replace(/[^0-9.]/g, ""));
    return Number.isFinite(n) ? n : 0;
  };
  const similarSlugs = new Set(similar.map((g) => g.slug));
  const topPrizeGames = more
    .filter((g) => prizeValue(g.top_prize) > 0 && !similarSlugs.has(g.slug))
    .sort((a, b) => prizeValue(b.top_prize) - prizeValue(a.top_prize))
    .slice(0, 5);


  return (
    <Layout>
      <SEOHead
        title={`${game.name} — ${stateName} Instant Game`}
        description={stripHtml(game.game_content ?? "", 160)}
      />
      <div className="container py-4 md:py-10 space-y-6 md:space-y-10">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/states">States</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
             <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/states/$stateSlug" params={{ stateSlug }}>
                  {stateName} Lottery
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{game.name}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Page header — mirrors state page */}
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="shrink-0">
            <StateLogo stateSlug={stateSlug} abbreviation={stateName.slice(0, 2).toUpperCase()} size="lg" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-xl sm:text-2xl md:text-3xl font-bold truncate">
              {game.name}
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground truncate">
              {stateName} Lottery instant game
            </p>
          </div>
        </div>

        {/* Hero card — light, matches site Card styling */}
        <Card className="overflow-hidden">
          <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
            <div className="bg-muted/40 flex items-center justify-center p-4 md:p-8 border-b md:border-b-0 md:border-r border-border">
              {game.image_url ? (
                <img
                  src={game.image_url}
                  alt={game.name}
                  className="max-h-[240px] sm:max-h-[320px] md:max-h-[360px] w-auto rounded-lg shadow-md"
                />
              ) : (
                <div className="w-full max-w-xs aspect-[3/4] rounded-lg bg-muted" />
              )}
            </div>

            <CardContent className="p-4 sm:p-6 md:p-8 flex flex-col justify-center">
              {game.categories.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {game.categories.slice(0, 3).map((c) => (
                    <span
                      key={c}
                      className="px-3 py-1 text-[11px] font-semibold rounded-full bg-accent/10 text-accent border border-accent/20 uppercase tracking-wide"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              )}

              <p className="font-display text-lg sm:text-xl md:text-2xl font-bold mb-4 md:mb-6 leading-tight break-words">
                {game.name} scratch-off ticket
                {game.price_range ? ` — ${game.price_range}` : ""}
              </p>

              <dl className="grid grid-cols-2 gap-3 sm:gap-4 mb-4 md:mb-6">
                {game.top_prize && (
                  <div className="rounded-lg border border-border bg-card p-3 sm:p-4 min-w-0">
                    <dt className="text-[10px] sm:text-xs uppercase font-semibold tracking-wide text-muted-foreground mb-1 flex items-center gap-1.5">
                      <Trophy className="h-3.5 w-3.5" /> Top Prize
                    </dt>
                    <dd className="font-display text-base sm:text-base md:text-base font-bold text-foreground break-words">
                      {game.top_prize}
                    </dd>
                  </div>
                )}
                {game.price_range && (
                  <div className="rounded-lg border border-border bg-card p-3 sm:p-4 min-w-0">
                    <dt className="text-[10px] sm:text-xs uppercase font-semibold tracking-wide text-muted-foreground mb-1 flex items-center gap-1.5">
                      <Ticket className="h-3.5 w-3.5" /> Play Amount
                    </dt>
                    <dd className="font-display text-base sm:text-base md:text-base font-bold text-foreground whitespace-nowrap">
                      {game.price_range}
                    </dd>
                  </div>
                )}
              </dl>

              {game.affiliate_link && (
                <Button
                  asChild
                  size="lg"
                  className="w-full h-12 rounded-full font-bold tracking-wide bg-accent text-white hover:bg-accent/90 hover:text-white"
                >
                  <a
                    href={game.affiliate_link}
                    target="_blank"
                    rel="noopener nofollow sponsored"
                  >
                    PLAY NOW
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                </Button>
              )}
            </CardContent>
          </div>
        </Card>

        {/* Details */}
        {body && (
          <section>
            <h2 className="font-display text-xl font-bold mb-4">About this game</h2>
            <Card>
              <CardContent className="p-4 sm:p-6 md:p-8">
                <article
                  className="instant-game-content"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(body) }}
                />
              </CardContent>
            </Card>
          </section>
        )}

        {/* FAQ */}
        {faq.length > 0 && (
          <section>
            <h2 className="font-display text-xl font-bold mb-4">
              Frequently Asked Questions
            </h2>
            <Card>
              <CardContent className="p-2 md:p-4">
                <Accordion type="single" collapsible className="w-full">
                  {faq.map((item, i) => (
                    <AccordionItem
                      key={i}
                      value={`faq-${i}`}
                      className="border-b border-border last:border-b-0"
                    >
                      <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline px-3 sm:px-4 py-4 text-sm sm:text-base">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="px-3 sm:px-4 pb-5">
                        <div
                          className="instant-game-content"
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.a) }}
                        />
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </section>
        )}

        {more.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
              <h2 className="font-display text-xl font-bold">More Instant Games</h2>
              <Button asChild variant="outline" size="sm" className="rounded-full">
                <Link
                  to="/states/$stateSlug"
                  params={{ stateSlug }}
                >
                  View all
                  <ArrowRight className="ml-1 h-3.5 w-3.5" />
                </Link>
              </Button>
            </div>
            <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {more.map((g, i) => (
                <div
                  key={g.slug}
                  className={i >= 12 ? "hidden lg:block" : i >= 5 ? "hidden sm:block" : ""}
                >
                  <InstantGameCard game={g} stateSlug={stateSlug} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Internal linking block — strengthens topical authority and gives AI
            answer engines descriptive anchors to cite this page from. */}
        <section aria-labelledby="related-heading">
          <h2
            id="related-heading"
            className="font-display text-xl font-bold mb-4"
          >
            Related on USA Lottery Club
          </h2>
          <Card>
            <CardContent className="p-4 sm:p-6 grid gap-6 md:grid-cols-2">
              <div>
                <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-3">
                  {stateName} Lottery
                </h3>
                <ul className="space-y-2 text-sm">
                  <li>
                    <Link
                      to="/states/$stateSlug"
                      params={{ stateSlug }}
                      className="text-accent hover:underline"
                    >
                      {stateName} lottery games, results & jackpots
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/states/$stateSlug"
                      params={{ stateSlug }}
                      className="text-accent hover:underline"
                    >
                      All {stateName} scratch-off instant games
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/tools/tax-calculator/$stateSlug"
                      params={{ stateSlug }}
                      className="text-accent hover:underline"
                    >
                      {stateName} lottery tax calculator
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/results"
                      className="text-accent hover:underline"
                    >
                      Latest US lottery winning numbers
                    </Link>
                  </li>
                </ul>
              </div>
              {(similar.length > 0 || topPrizeGames.length > 0) && (
                <div className="space-y-6">
                  {similar.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-3">
                        Similar {stateName} scratch-offs
                      </h3>
                      <ul className="space-y-2 text-sm">
                        {similar.map((g) => (
                          <li key={g.slug}>
                            <Link
                              to="/states/$stateSlug/instant/$gameSlug"
                              params={{ stateSlug, gameSlug: g.slug }}
                              className="text-accent hover:underline"
                            >
                              {g.name} {stateName} scratch-off
                              {g.price_range ? ` (${g.price_range})` : ""}
                              {g.top_prize ? ` — top prize ${g.top_prize}` : ""}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {topPrizeGames.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-3">
                        Biggest {stateName} instant-game top prizes
                      </h3>
                      <ul className="space-y-2 text-sm">
                        {topPrizeGames.map((g) => (
                          <li key={g.slug}>
                            <Link
                              to="/states/$stateSlug/instant/$gameSlug"
                              params={{ stateSlug, gameSlug: g.slug }}
                              className="text-accent hover:underline"
                            >
                              {g.name} — win up to {g.top_prize} on this {stateName}{" "}
                              instant game
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </section>
      </div>
    </Layout>
  );
}